"use client"

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ErrorBar,
} from "recharts"
import { type ChartDimension } from "./types"
import type { CSSProperties } from "react"

/** A single category for a box plot — five-number summary. */
export interface BoxPlotRow {
  name: string
  min: number
  q1: number
  median: number
  q3: number
  max: number
  /** Optional point list overlaid as outlier dots. */
  outliers?: number[]
}

export interface BoxPlotChartProps {
  data: BoxPlotRow[]
  color?: string
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  xLabel?: string
  yLabel?: string
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
}

export function BoxPlotChart({
  data, color = "#3b82f6",
  showLegend = false, showGrid = true, showTooltip = true,
  xLabel, yLabel, className, style, height = "100%", width = "100%",
}: BoxPlotChartProps) {
  // Encode the five-number summary as two stacked bars. The bottom bar
  // (`bottomBox`) goes from q1 to median; the top bar (`topBox`) from
  // median to q3. Whiskers are added via ErrorBar.
  const rows = data.map(r => ({
    name: r.name,
    base: r.q1,                           // invisible padding to start at q1
    bottomBox: r.median - r.q1,           // q1..median
    topBox: r.q3 - r.median,              // median..q3
    whiskerLow: r.q1 - r.min,
    whiskerHigh: r.max - r.q3,
  }))

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        <XAxis dataKey="name" tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />
        <YAxis tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar dataKey="base" stackId="b" fill="transparent" isAnimationActive={false} legendType="none" />
        <Bar dataKey="bottomBox" stackId="b" fill={color} fillOpacity={0.7} isAnimationActive={false} legendType="none" />
        <Bar dataKey="topBox" stackId="b" fill={color} fillOpacity={0.5} isAnimationActive={false} legendType="none">
          <ErrorBar dataKey="whiskerHigh" width={10} stroke={color} strokeWidth={1.2} direction="y" />
          {/* Cells just to keep recharts happy when there's a single series */}
          {rows.map((_, i) => <Cell key={i} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
